import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../api/client.js';
import { Loader, Alert, Stat, formatDate, relativeTime } from '../../components/ui.jsx';
import CubMascot from '../../components/CubMascot.jsx';
import { Sticker } from '../../lessons/activities.jsx';
import { AuditSummary } from '../../lessons/practical.jsx';
import { useSchoolScope } from './SchoolScope.jsx';

const STATUS_PILL = {
  completed: { cls: 'pill-good', text: '✓ Completed' },
  in_progress: { cls: 'pill-warn', text: 'In progress' },
};

export default function AdminStudentDetail() {
  const { id } = useParams();
  const { schoolId } = useSchoolScope();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.adminStudent(id, schoolId).then(setData).catch((err) => setError(err.message));
  }, [id, schoolId]);

  if (error) {
    return (
      <>
        <Alert kind="bad">{error}</Alert>
        <Link to="/admin/students" className="btn btn-ghost">← Back to students</Link>
      </>
    );
  }
  if (!data) return <Loader label="Loading student record…" />;

  const { student, lessons, attempts, badges, logins, projects = [] } = data;
  const completed = lessons.filter((l) => l.status === 'completed').length;
  const xp = lessons.reduce((sum, l) => sum + Number(l.xp_earned ?? 0), 0);
  const minutes = lessons.reduce((sum, l) => sum + Number(l.minutes ?? 0), 0);

  return (
    <>
      <div className="page-head">
        <div className="row gap-md">
          <CubMascot avatar={student.avatar} size={72} mood="happy" />
          <div>
            <h1 style={{ marginBottom: '0.1em' }}>{student.fullName}</h1>
            <p style={{ margin: 0 }}>
              <strong>{student.userId}</strong> · {student.grade ?? 'No grade'} ·{' '}
              {student.schoolName}
              {!student.isActive && <span className="pill pill-bad" style={{ marginLeft: 8 }}>Paused</span>}
            </p>
            {student.parentMobile && (
              <p className="muted small" style={{ margin: '0.25rem 0 0' }}>
                📱 Parent contact: <strong>{student.parentMobile}</strong>
              </p>
            )}
          </div>
        </div>
        <Link to="/admin/students" className="btn btn-ghost">← Back to students</Link>
      </div>

      <div className="stat-grid">
        <Stat icon="✅" tone="mint"  value={`${completed}/${lessons.length}`} label="Lessons completed" />
        <Stat icon="⭐" tone="sun"   value={xp} label="XP earned" />
        <Stat icon="🏅" tone="grape" value={badges.length} label="Badges" />
        <Stat icon="⏱" tone="sky"   value={`${minutes} min`} label="Time learning" />
      </div>

      <div className="panel-grid mt-lg">
        <section className="card-flat">
          <h2>Lesson by lesson</h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lesson</th>
                  <th>Status</th>
                  <th className="num">Score</th>
                  <th className="num">Tries</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {lessons.map((l) => {
                  const pill = STATUS_PILL[l.status];
                  return (
                    <tr key={l.slug}>
                      <td>
                        <strong><span aria-hidden="true">{l.icon}</span> {l.title}</strong>
                        <span className="faint small" style={{ display: 'block' }}>{l.moduleTitle}</span>
                      </td>
                      <td>
                        <span className={`pill ${pill?.cls ?? ''}`}>{pill?.text ?? 'Not started'}</span>
                      </td>
                      <td className="num">
                        {l.best_score != null && l.total_questions
                          ? `${l.best_score}/${l.total_questions}`
                          : '—'}
                      </td>
                      <td className="num">{l.attempts ?? 0}</td>
                      <td className="small muted">{l.completed_at ? formatDate(l.completed_at) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <div className="stack gap-md">
          {projects.map((proj) => (
            <section className="card-flat" key={proj.lessonTitle}>
              <h2>
                <span aria-hidden="true">{proj.lessonIcon}</span> Practical task
              </h2>
              <p className="muted small">
                {proj.lessonTitle} · updated {formatDate(proj.updatedAt, true)}
              </p>
              {proj.kind === 'sticker-pack' ? (
                <div className="pack-view">
                  {proj.items.map((s, i) => (
                    <Sticker key={i} sticker={s} size={104} />
                  ))}
                </div>
              ) : proj.kind === 'device-audit' ? (
                <AuditSummary items={proj.items} />
              ) : (
                <p className="faint small">{proj.items.length} item(s) saved.</p>
              )}
            </section>
          ))}

          <section className="card-flat">
            <h2>Badges earned</h2>
            {badges.length === 0 ? (
              <p className="faint">No badges yet.</p>
            ) : (
              <div className="row wrap gap-sm">
                {badges.map((b) => (
                  <div key={b.slug} className="badge-chip is-earned" title={b.description}>
                    <span className="badge-icon" aria-hidden="true">{b.icon}</span>
                    <span className="badge-name">{b.title}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="card-flat">
            <h2>Recent quiz attempts</h2>
            {attempts.length === 0 ? (
              <p className="faint">No quiz attempts yet.</p>
            ) : (
              <ul className="login-history">
                {attempts.map((a, i) => (
                  <li key={i}>
                    <span><span aria-hidden="true">{a.icon}</span> {a.title}</span>
                    <span className="muted small">
                      {a.score}/{a.total} · {relativeTime(a.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card-flat">
            <h2>Sign-in history</h2>
            {logins.length === 0 ? (
              <p className="faint">Never signed in.</p>
            ) : (
              <ul className="login-history">
                {logins.map((l, i) => (
                  <li key={i}>
                    <span>{formatDate(l.created_at, true)}</span>
                    <span className={l.success ? 'pill pill-good' : 'pill pill-bad'}>
                      {l.success ? 'Success' : 'Failed'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
